import { useState, useEffect } from "react";
import { Layers, X, Plus } from "lucide-react";
import type { BatchResponse } from "../../gen/models";

interface ActiveBatchSelectModalProps {
  isOpen: boolean;
  activeBatches: BatchResponse[];
  activeBatchId: string;
  onClose: () => void;
  onSelectBatch: (batchId: string) => void;
  onCreateBatch: (title: string, description?: string, assignAllEvaluations?: boolean) => Promise<string | null>;
}

export function ActiveBatchSelectModal({
  isOpen,
  activeBatches,
  activeBatchId,
  onClose,
  onSelectBatch,
  onCreateBatch,
}: ActiveBatchSelectModalProps) {
  const [selectedId, setSelectedId] = useState(activeBatchId || (activeBatches[0]?.id ?? ""));
  const [isCreating, setIsCreating] = useState(activeBatches.length === 0);
  const [newTitle, setNewTitle] = useState("");
  const [newDescription, setNewDescription] = useState("");
  const [assignAllEvaluations, setAssignAllEvaluations] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const getDefaultBatchTitle = () => {
    const now = new Date();
    const dateStr = now.toISOString().split("T")[0];
    const timeStr = now.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
    return `Batch ${dateStr} ${timeStr}`;
  };

  useEffect(() => {
    if (isOpen) {
      setAssignAllEvaluations(true);
      if (activeBatches.length === 0) {
        setIsCreating(true);
        setNewTitle(getDefaultBatchTitle());
      } else {
        setIsCreating(false);
        setSelectedId(activeBatchId && activeBatches.some((b) => b.id === activeBatchId) ? activeBatchId : activeBatches[0].id);
      }
      setNewDescription("");
      setSubmitting(false);
    }
  }, [isOpen, activeBatches, activeBatchId]);

  if (!isOpen) return null;

  const handleConfirmSelect = () => {
    if (selectedId) {
      onSelectBatch(selectedId);
      onClose();
    }
  };

  const handleConfirmCreate = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    const titleToUse = newTitle.trim() || getDefaultBatchTitle();
    setSubmitting(true);
    try {
      const createdId = await onCreateBatch(
        titleToUse,
        newDescription.trim() || undefined,
        assignAllEvaluations
      );
      if (createdId) {
        onSelectBatch(createdId);
        onClose();
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4">
      <div className="bg-stone-900 border border-stone-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden p-5 space-y-4">
        <div className="flex items-center justify-between border-b border-stone-800 pb-3">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-emerald-400" />
            <h3 className="text-sm font-bold text-stone-100">
              {activeBatches.length === 0
                ? "Create Active Batch"
                : isCreating
                ? "Create New Batch"
                : "Select Active Batch"}
            </h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-stone-400 hover:text-stone-200 p-1"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {activeBatches.length === 0 ? (
          <div className="space-y-4 text-xs">
            <div className="rounded-xl border border-amber-900/40 bg-amber-950/20 p-3 text-amber-300 space-y-1">
              <p className="font-semibold">There are no active batches.</p>
              <p className="text-stone-400">
                Comments and notes must be assigned to an active collecting batch. Do you want to create a new one?
              </p>
            </div>

            <form onSubmit={handleConfirmCreate} className="space-y-3">
              <div>
                <label className="block font-semibold text-stone-300 mb-1">
                  Batch Name
                </label>
                <input
                  type="text"
                  value={newTitle}
                  onChange={(e) => setNewTitle(e.target.value)}
                  placeholder={getDefaultBatchTitle()}
                  className="w-full rounded-lg border border-stone-800 bg-stone-950 p-2 text-xs text-stone-100 focus:outline-hidden focus:border-stone-600"
                  autoFocus
                />
              </div>

              <div>
                <label className="block font-semibold text-stone-300 mb-1">
                  Description (Optional)
                </label>
                <textarea
                  rows={2}
                  value={newDescription}
                  onChange={(e) => setNewDescription(e.target.value)}
                  placeholder="Notes, targets, or agent review context..."
                  className="w-full rounded-lg border border-stone-800 bg-stone-950 p-2 text-xs text-stone-100 focus:outline-hidden focus:border-stone-600"
                />
              </div>

              {/* Assign all evaluations checkbox */}
              <div className="p-3 rounded-xl border border-stone-800 bg-stone-950/60 flex items-center justify-between">
                <div>
                  <div className="font-semibold text-stone-200">
                    Assign all evaluations to batch
                  </div>
                  <p className="text-[11px] text-stone-400 mt-0.5">
                    Attaches all evaluations on this thread to the new batch upon creation.
                  </p>
                </div>
                <input
                  type="checkbox"
                  checked={assignAllEvaluations}
                  onChange={(e) => setAssignAllEvaluations(e.target.checked)}
                  className="w-4 h-4 accent-emerald-500 rounded cursor-pointer shrink-0 ml-3"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t border-stone-800">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded-lg border border-stone-800 text-stone-400 hover:text-stone-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? "Creating..." : "Create & Set Active"}
                </button>
              </div>
            </form>
          </div>
        ) : isCreating ? (
          <form onSubmit={handleConfirmCreate} className="space-y-3 text-xs">
            <div>
              <label className="block font-semibold text-stone-300 mb-1">
                Batch Name
              </label>
              <input
                type="text"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
                placeholder={getDefaultBatchTitle()}
                className="w-full rounded-lg border border-stone-800 bg-stone-950 p-2 text-xs text-stone-100 focus:outline-hidden focus:border-stone-600"
                autoFocus
              />
            </div>

            <div>
              <label className="block font-semibold text-stone-300 mb-1">
                Description (Optional)
              </label>
              <textarea
                rows={2}
                value={newDescription}
                onChange={(e) => setNewDescription(e.target.value)}
                placeholder="Context or notes..."
                className="w-full rounded-lg border border-stone-800 bg-stone-950 p-2 text-xs text-stone-100 focus:outline-hidden focus:border-stone-600"
              />
            </div>

            {/* Assign all evaluations checkbox */}
            <div className="p-3 rounded-xl border border-stone-800 bg-stone-950/60 flex items-center justify-between">
              <div>
                <div className="font-semibold text-stone-200">
                  Assign all evaluations to batch
                </div>
                <p className="text-[11px] text-stone-400 mt-0.5">
                  Attaches all evaluations on this thread to the new batch upon creation.
                </p>
              </div>
              <input
                type="checkbox"
                checked={assignAllEvaluations}
                onChange={(e) => setAssignAllEvaluations(e.target.checked)}
                className="w-4 h-4 accent-emerald-500 rounded cursor-pointer shrink-0 ml-3"
              />
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-stone-800">
              <button
                type="button"
                onClick={() => setIsCreating(false)}
                className="text-stone-400 hover:text-stone-200 text-xs"
              >
                Back to Selection
              </button>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded-lg border border-stone-800 text-stone-400 hover:text-stone-200"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold flex items-center gap-1.5 disabled:opacity-50 cursor-pointer"
                >
                  {submitting ? "Creating..." : "Create & Set Active"}
                </button>
              </div>
            </div>
          </form>
        ) : (
          <div className="space-y-4 text-xs">
            <p className="text-stone-400">
              Please choose an active collecting batch for your comments and notes:
            </p>

            <div>
              <label className="block font-semibold text-stone-300 mb-1">
                Active Batch
              </label>
              <select
                value={selectedId}
                onChange={(e) => setSelectedId(e.target.value)}
                className="w-full rounded-lg border border-stone-800 bg-stone-950 p-2 text-xs text-stone-100 focus:outline-hidden focus:border-stone-600"
              >
                {activeBatches.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.title} (collecting)
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-between items-center pt-2 border-t border-stone-800">
              <button
                type="button"
                onClick={() => {
                  setNewTitle(getDefaultBatchTitle());
                  setIsCreating(true);
                }}
                className="inline-flex items-center gap-1 text-emerald-400 hover:underline cursor-pointer"
              >
                <Plus className="w-3 h-3" />
                <span>+ Create new batch instead</span>
              </button>

              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-3 py-1.5 rounded-lg border border-stone-800 text-stone-400 hover:text-stone-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={handleConfirmSelect}
                  disabled={!selectedId}
                  className="px-4 py-1.5 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold disabled:opacity-50 cursor-pointer"
                >
                  Confirm Active Batch
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
